import React from 'react';
import PropTypes from 'prop-types';
import TextareaAutosize from 'react-textarea-autosize';

import CustomScrollbar, {CustomScrollbarTrackVisibility, CustomScrollbarProps} from '../../../lib/custom-scrollbar/custom-scrollbar';
import OEResizeObserver from '../../../lib/components/oe-resize-observer';
import OEToolbox from '../../../lib/tools/oe-toolbox';

export default class OETextEdit extends React.PureComponent {

    constructor(props) {
        super(props);

        this.unitSize = 14;

        this.style = this.determineTextElementStyle();

        this.state = {
            style: this.style
        };

        this.onKeyDown = this.onKeyDown.bind(this);

        this.onUnitSizeResize = this.onUnitSizeResize.bind(this);

        this.onTextElementRef = this.onTextElementRef.bind(this);

        this.onChange = this.onChange.bind(this);
        this.onFocus = this.onFocus.bind(this);
        this.onBlur = this.onBlur.bind(this);
    }

    componentWillReceiveProps(nextProps) {
        if(nextProps.fontSize !== this.props.fontSize || nextProps.autosize !== this.props.autosize || nextProps.singleLine !== this.props.singleLine || !OEToolbox.shallowEqual(nextProps.textElementStyle, this.props.textElementStyle, true) || !OEToolbox.shallowEqual(nextProps.scrollbar, this.props.scrollbar, true)) {
            this.updateTextElementStyle(nextProps);
        }
    }

    determineTextElementStyle(props)   {
        props = props || this.props;
        let textElementStyle = {};

        let scale = 1;

        if(props.fontSize)  {
            textElementStyle.fontSize = props.fontSize;
            scale = this.unitSize / props.fontSize;
        }

        if(props.autosize && !props.singleLine && props.scrollbar.verticalTrackVisiblity != CustomScrollbarTrackVisibility.none)  textElementStyle.paddingRight = (scale * 0.6428571429).toString() + 'em';

        if(props.textElementStyle) Object.assign(textElementStyle, props.textElementStyle);

        return textElementStyle;
    }

    updateTextElementStyle(props)  {
        let style = this.determineTextElementStyle(props);
        if(OEToolbox.shallowEqual(style, this.style, true))  return;
        this.style = style;
        this.setState({style: this.style});
    }

    focus() {
        if(!this.textElementRef)   return;
        this.textElementRef.focus();
    }

    scrollToEnd()   {
        if(!this.textElementRef)   return;
        this.textElementRef.scrollLeft = this.textElementRef.scrollWidth;
        this.textElementRef.setSelectionRange(this.textElementRef.value.length, this.textElementRef.value.length);
    }

    onKeyDown(e)    {
        if(this.props.disabled) return;

        if(this.props.onKeyDown)    this.props.onKeyDown(this, e);

        if(e.key !== 'Tab') return;
        e.preventDefault();
        let element = e.target;

        let start = element.selectionStart;
        let end = element.selectionEnd;
        element.value = element.value.substring(0, start) + '\t' + element.value.substring(end);

        element.selectionStart = element.selectionEnd = start + 1;

        this.onChange(e);
    }

    onUnitSizeResize(sender, size)  {
        if(this.unitSize === size.w) return;
        this.unitSize = size.w;
        this.updateTextElementStyle();
    }

    onTextElementRef(ref) {
        if(this.textElementRef === ref)    return;

        if(this.textElementRef)    this.textElementRef.removeEventListener('keydown', this.onKeyDown);

        this.textElementRef = ref;

        if(this.textElementRef)    {
            this.textElementRef.addEventListener('keydown', this.onKeyDown);

            if(this.props.autofocus)    this.focus();
            if(this.props.scrollToEnd)  this.scrollToEnd();
        }
    }

    renderTextElement()    {

        if(this.props.singleLine)   {
            return (
                <input
                    style={this.state.style}
                    disabled={this.props.disabled}
                    spellCheck={this.props.spellCheck}
                    placeholder={this.props.placeholder}
                    maxLength={this.props.maxTextLength ? this.props.maxTextLength : undefined}
                    value={this.props.value}
                    onChange={this.onChange}
                    onFocus={this.onFocus}
                    onBlur={this.onBlur}
                    ref={this.onTextElementRef}
                />
            );
        }

        if(this.props.autosize)   {
            return (
                <CustomScrollbar
                    {...this.props.scrollbar}
                >
                    <TextareaAutosize
                        style={this.state.style}
                        disabled={this.props.disabled}
                        spellCheck={this.props.spellCheck}
                        placeholder={this.props.placeholder}
                        maxLength={this.props.maxTextLength ? this.props.maxTextLength : undefined}
                        value={this.props.value}
                        onChange={this.onChange}
                        onFocus={this.onFocus}
                        onBlur={this.onBlur}
                        ref={this.onTextElementRef}
                    />
                </CustomScrollbar>
            );
        }

        return (
            <textarea
                style={this.state.style}
                disabled={this.props.disabled}
                spellCheck={this.props.spellCheck}
                placeholder={this.props.placeholder}
                maxLength={this.props.maxTextLength ? this.props.maxTextLength : undefined}
                value={this.props.value}
                onChange={this.onChange}
                onFocus={this.onFocus}
                onBlur={this.onBlur}
                ref={this.onTextElementRef}
            />
        );
    }

    render()    {
        const className = 'text-edit ' + (this.props.autosize && !this.props.singleLine ? 'autosize ' : '') + (this.props.singleLine ? 'single-line ' : '') + (this.props.disabled ? 'disabled ' : '') + this.props.className;

        return (
            <div 
                className={className}
                style={this.props.style}
            >
                <OEResizeObserver className="unit-size-observer" onResize={this.onUnitSizeResize}/>

                <div className="text-edit-container">
                    {this.renderTextElement()}
                </div>
            </div>
        );
    }

    onChange(evt)   {
        if(this.props.disabled || !this.props.onChange) return;
        this.props.onChange(this, evt.target.value);
    }

    onFocus(evt)   {
        if(this.props.onFocus)  this.props.onFocus(this);
    }

    onBlur(evt)   {
        if(this.props.onBlur)  this.props.onBlur(this);
    }
}

OETextEdit.defaultProps = {
    className:                          '',
    autosize:                           true,
    disabled:                           false,
    spellCheck:                         false,
    placeholder:                        '',
    singleLine:                         false,
    autofocus:                          false,
    scrollToEnd:                        false,
    value:                              '',
    scrollbar: {
        bare:                           true,               // without bare minSize for textarea element does not work for autosize=true path
        horizontalTrackVisiblity:       CustomScrollbarTrackVisibility.none,
        verticalTrackVisiblity:         CustomScrollbarTrackVisibility.always,
    }
};

OETextEdit.propTypes = {
    className:                          PropTypes.string,
    style:                              PropTypes.object,
    textElementStyle:                   PropTypes.object,
    autosize:                           PropTypes.bool,
    disabled:                           PropTypes.bool,
    spellCheck:                         PropTypes.bool,
    placeholder:                        PropTypes.string,
    singleLine:                         PropTypes.bool,
    autofocus:                          PropTypes.bool,
    scrollToEnd:                        PropTypes.bool,
    maxTextLength:                      PropTypes.number,
    fontSize:                           PropTypes.number,
    value:                              PropTypes.string,
    scrollbar:                          CustomScrollbarProps.propsDecl,
    onChange:                           PropTypes.func,
    onFocus:                            PropTypes.func,
    onBlur:                             PropTypes.func,
    onKeyDown:                          PropTypes.func
};